import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import {
  ArrowLeft, Pill, AlertTriangle, Clock,
  Shield, Brain, Activity, Heart, Zap, CheckCircle2,
  Info, Utensils, Dumbbell,
  BookOpen, FlaskConical, Stethoscope, Droplets, Scale
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';

const DIABETES_PROTOCOL = [
  {
    name: 'Berberine',
    dosage: '500mg, 2-3 times daily',
    timing: 'Before meals (15-30 min)',
    icon: Zap,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    evidence: 'A',
    why: 'Berberine activates AMPK — the same metabolic pathway targeted by Metformin. Meta-analyses of 14+ RCTs show it reduces fasting blood glucose by 15-25%, HbA1c by 0.5-0.9%, and improves insulin sensitivity. Also lowers LDL cholesterol and triglycerides.',
    interactions: ['May ENHANCE effects of Metformin — monitor for low blood sugar', 'May interact with CYP3A4 medications (statins, some blood pressure drugs)', 'Do NOT combine with cyclosporine'],
    notes: 'Start with 500mg once daily and increase gradually to minimize GI side effects. Berberine HCl is the standard form. Effects are dose-dependent — 1,500mg/day shows best results.'
  },
  {
    name: 'Alpha-Lipoic Acid (ALA)',
    dosage: '300-600mg daily',
    timing: 'On empty stomach, 30 min before meals',
    icon: Shield,
    color: 'bg-green-100 text-green-700 border-green-200',
    evidence: 'A',
    why: 'ALA improves insulin sensitivity by enhancing glucose uptake in muscle cells. It crosses the blood-brain barrier, protecting against diabetic neuropathy. The ALADIN and SYDNEY trials showed significant improvement in neuropathy symptoms (pain, burning, numbness) within 3-5 weeks.',
    interactions: ['May lower blood sugar — monitor closely with insulin/sulfonylureas', 'Safe with Metformin', 'May chelate minerals — take separately from iron/zinc supplements'],
    notes: 'R-Alpha Lipoic Acid (R-ALA) is the bioactive form with better absorption. If you have diabetic neuropathy, 600mg/day is the studied dose.'
  },
  {
    name: 'Chromium Picolinate',
    dosage: '200-1,000mcg daily',
    timing: 'With meals',
    icon: Activity,
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    evidence: 'A',
    why: 'Chromium enhances insulin signaling by potentiating insulin receptor activity. Multiple meta-analyses show it significantly reduces fasting glucose and HbA1c in Type 2 diabetes. Also helps reduce carbohydrate cravings and supports healthy body composition.',
    interactions: ['May enhance insulin/diabetes medication effects — monitor blood sugar', 'Safe with Metformin', 'Picolinate form has best absorption'],
    notes: 'Picolinate form is 2-3x better absorbed than other chromium forms. Start with 200mcg and titrate up. Higher doses (1,000mcg) used in clinical trials for Type 2 diabetes.'
  },
  {
    name: 'Magnesium Glycinate',
    dosage: '300-500mg elemental magnesium daily',
    timing: 'Evening, with dinner or before bed',
    icon: Heart,
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    evidence: 'A',
    why: 'Up to 75% of diabetics are magnesium deficient. Magnesium is a cofactor for 300+ enzymatic reactions including insulin signaling. Meta-analyses show supplementation improves fasting glucose, insulin sensitivity, and HbA1c. Also reduces cardiovascular risk — the leading cause of death in diabetes.',
    interactions: ['Safe with all diabetes medications', 'Separate from antibiotics by 2+ hours', 'May enhance blood pressure medications slightly'],
    notes: 'Glycinate form is best absorbed and least likely to cause diarrhea. Test magnesium RBC levels (not serum) for accurate assessment. Aim for 400-500mg if severely deficient.'
  },
  {
    name: 'Omega-3 (High EPA/DHA)',
    dosage: '2,000-4,000mg combined EPA/DHA',
    timing: 'With meals containing fat, split AM/PM',
    icon: Heart,
    color: 'bg-rose-100 text-rose-700 border-rose-200',
    evidence: 'B+',
    why: 'Omega-3s reduce the chronic low-grade inflammation driving insulin resistance. They significantly lower triglycerides (often elevated in diabetes) and reduce cardiovascular risk. Studies show improved endothelial function and reduced arterial stiffness in diabetic patients.',
    interactions: ['May slightly increase fasting glucose at very high doses (>4g) — monitor', 'Safe with all diabetes medications', 'May enhance blood thinners'],
    notes: 'Focus on EPA-dominant formulas for best anti-inflammatory and triglyceride-lowering effect. IFOS-certified for purity. Triglyceride form better absorbed than ethyl ester.'
  },
  {
    name: 'Cinnamon Extract (Ceylon)',
    dosage: '500-1,000mg Ceylon cinnamon extract daily',
    timing: 'With meals, split into 2 doses',
    icon: Utensils,
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    evidence: 'B+',
    why: 'Ceylon cinnamon improves insulin sensitivity by activating insulin receptors and enhancing glucose transporter activity. Meta-analyses show modest but significant reductions in fasting glucose (10-25 mg/dL) and HbA1c. Also reduces postprandial glucose spikes.',
    interactions: ['Safe with most diabetes medications', 'Use CEYLON (not Cassia) — Cassia contains coumarin which can damage liver at high doses', 'May enhance blood thinners slightly'],
    notes: 'Ceylon (Cinnamomum verum) is critical — Cassia cinnamon has toxic coumarin levels when used as a supplement. Look for standardized extract, not powder.'
  },
  {
    name: 'Vitamin D3',
    dosage: '2,000-5,000 IU daily',
    timing: 'Morning with a fat-containing meal',
    icon: Zap,
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    evidence: 'B+',
    why: 'Vitamin D deficiency is strongly linked to insulin resistance and increased Type 2 diabetes risk. D3 supports beta-cell function (the cells that produce insulin) and modulates immune responses involved in Type 1 diabetes. Supplementation improves insulin sensitivity in deficient individuals.',
    interactions: ['Safe with all diabetes medications', 'Enhances calcium absorption', 'Metformin may impair D3 absorption — supplementation extra important'],
    notes: 'Get levels tested — aim for 40-60 ng/mL. Many diabetic patients are severely deficient. Metformin can reduce B12 AND D3 absorption.'
  },
  {
    name: 'Probiotics (Multi-Strain)',
    dosage: '30-50 billion CFU daily',
    timing: 'Morning on empty stomach, 30 min before food',
    icon: FlaskConical,
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    evidence: 'B',
    why: 'The gut microbiome directly influences blood sugar regulation and insulin sensitivity. Meta-analyses show probiotics reduce fasting glucose, HbA1c, and insulin resistance in Type 2 diabetes. They also reduce inflammation markers (CRP, TNF-alpha) that drive diabetic complications.',
    interactions: ['Safe with all diabetes medications', 'Separate from antibiotics by 2+ hours', 'May enhance immune function'],
    notes: 'Look for strains: L. acidophilus, L. rhamnosus, B. lactis, B. longum. Multi-strain formulas outperform single strains. Refrigerated brands maintain potency better.'
  }
];

const COMMON_MEDICATIONS = [
  { name: 'Metformin', notes: 'All supplements are safe. Berberine works synergistically — monitor blood sugar for lows. Metformin depletes B12 and may impair D3 absorption — supplementation important. Magnesium enhances Metformin efficacy.' },
  { name: 'Insulin (all types)', notes: 'Berberine, ALA, and chromium may LOWER blood sugar — monitor closely and adjust insulin doses with your doctor. All other supplements are safe.' },
  { name: 'Sulfonylureas (Glipizide, Glimepiride)', notes: 'Berberine and ALA can enhance blood sugar lowering — risk of hypoglycemia. Start supplements at lowest dose and monitor. Chromium may also add to effects.' },
  { name: 'GLP-1 Agonists (Ozempic, Mounjaro)', notes: 'Generally safe with all supplements. Berberine has complementary mechanisms. Probiotics may help with GI side effects common with GLP-1 drugs.' },
  { name: 'SGLT2 Inhibitors (Jardiance, Farxiga)', notes: 'Safe with all supplements. Magnesium especially important as SGLT2 inhibitors can deplete electrolytes. Stay well hydrated.' },
  { name: 'Statins', notes: 'Berberine may interact (both use CYP3A4 pathway) — discuss with doctor. Omega-3 safely complements statin therapy for lipid management. CoQ10 recommended if taking statins.' }
];

const LIFESTYLE_TIPS = [
  {
    icon: Utensils,
    title: 'Blood Sugar-Friendly Diet',
    content: 'Prioritize low-glycemic foods: non-starchy vegetables, legumes, nuts, seeds, lean proteins, and whole grains. Pair carbs with protein/fat to blunt glucose spikes. Consider time-restricted eating (16:8). The Mediterranean and DASH diets show the strongest evidence for diabetes management.'
  },
  {
    icon: Dumbbell,
    title: 'Exercise for Insulin Sensitivity',
    content: 'Combination of resistance training (3x/week) and aerobic exercise (150 min/week) is most effective. A 10-minute walk after meals dramatically reduces postprandial glucose spikes. Even standing after meals helps. Consistency matters more than intensity.'
  },
  {
    icon: Scale,
    title: 'Weight Management',
    content: 'Losing just 5-7% of body weight significantly improves insulin sensitivity and can put Type 2 diabetes into remission. Focus on sustainable changes, not crash diets. Visceral fat (belly fat) is the most metabolically dangerous — targeted by exercise and low-carb approaches.'
  },
  {
    icon: Activity,
    title: 'Blood Sugar Monitoring',
    content: 'Track fasting and post-meal glucose when starting new supplements. CGMs (continuous glucose monitors) provide the most actionable data. Log results alongside meals, exercise, and supplements to identify what works for YOUR body.'
  },
  {
    icon: Brain,
    title: 'Stress & Sleep Optimization',
    content: 'Cortisol from chronic stress directly raises blood sugar. Practice stress management daily: deep breathing, meditation, or nature walks. Aim for 7-8 hours of quality sleep — even one night of poor sleep impairs insulin sensitivity by 25-30%.'
  }
];

const EvidenceGrade = ({ grade }) => {
  const colors = {
    'A': 'bg-green-100 text-green-800 border-green-300',
    'B+': 'bg-blue-100 text-blue-800 border-blue-300',
    'B': 'bg-sky-100 text-sky-800 border-sky-300'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${colors[grade] || colors['B']}`} data-testid={`evidence-grade-${grade}`}>
      Evidence: {grade}
    </span>
  );
};

export default function DiabetesGuidePage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary" data-testid="diabetes-guide-back">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="diabetes-guide-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <Droplets className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">
            Diabetes & Blood Sugar Guide
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto text-sm sm:text-base">
            An evidence-based supplement protocol for Type 2 Diabetes and blood sugar management — covering insulin sensitivity, glucose control, and complication prevention.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-8" data-testid="diabetes-guide-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-900 mb-1">Critical Medical Disclaimer</p>
              <p className="text-xs text-red-800">
                Several supplements in this guide can LOWER blood sugar. If you take insulin or sulfonylureas, monitor glucose closely when starting any new supplement and discuss with your endocrinologist.
                Never stop or adjust prescribed diabetes medication without medical guidance. These supplements support — but do not replace — your treatment plan.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Pill className="w-5 h-5 text-primary" />
            Blood Sugar Support Protocol
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            8 evidence-based supplements ranked by research strength. Start with one at a time, monitor blood sugar, and add gradually.
          </p>

          <div className="bg-white rounded-2xl border border-border p-5 mb-6" data-testid="daily-schedule">
            <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Optimized Daily Schedule
            </h3>
            <div className="space-y-3">
              {[
                { time: 'Morning (before breakfast)', supps: 'Berberine (500mg), Probiotics (30 min before food), ALA (empty stomach)', color: 'bg-amber-50 border-amber-200 text-amber-900' },
                { time: 'With breakfast', supps: 'Vitamin D3, Chromium, Omega-3, Cinnamon Extract', color: 'bg-sky-50 border-sky-200 text-sky-900' },
                { time: 'Before lunch', supps: 'Berberine (500mg)', color: 'bg-orange-50 border-orange-200 text-orange-900' },
                { time: 'Evening', supps: 'Berberine (500mg, before dinner), Magnesium Glycinate (before bed), Omega-3 (2nd dose with dinner)', color: 'bg-indigo-50 border-indigo-200 text-indigo-900' }
              ].map((slot, i) => (
                <div key={i} className={`rounded-xl border p-3 ${slot.color}`}>
                  <p className="text-xs font-semibold">{slot.time}</p>
                  <p className="text-xs mt-1">{slot.supps}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <Accordion type="multiple" className="space-y-3">
              {DIABETES_PROTOCOL.map((supp, index) => {
                const Icon = supp.icon;
                return (
                  <motion.div key={supp.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * index }}>
                    <AccordionItem value={supp.name} className="bg-white rounded-xl border border-border overflow-hidden">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50/50">
                        <div className="flex items-center gap-3 text-left">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${supp.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-sm text-[#1A1A1A]">{supp.name}</h3>
                              <EvidenceGrade grade={supp.evidence} />
                            </div>
                            <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{supp.dosage}</span>
                              <span>{supp.timing}</span>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-4 pt-2">
                          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                            <h4 className="text-sm font-medium text-blue-900 mb-1 flex items-center gap-2">
                              <FlaskConical className="w-4 h-4" />
                              Why This Matters for Blood Sugar
                            </h4>
                            <p className="text-xs text-blue-800">{supp.why}</p>
                          </div>
                          {supp.interactions.length > 0 && (
                            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                              <h4 className="text-sm font-medium text-red-900 mb-2 flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                Medication Interactions
                              </h4>
                              <ul className="space-y-1">
                                {supp.interactions.map((int_, i) => (
                                  <li key={i} className="text-xs text-red-800 flex items-start gap-2">
                                    <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    {int_}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {supp.notes && (
                            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                Clinical Note
                              </h4>
                              <p className="text-xs text-gray-700">{supp.notes}</p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                );
              })}
            </Accordion>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-primary" />
            Medication Compatibility Guide
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            How these supplements interact with common diabetes medications.
          </p>
          <div className="space-y-3" data-testid="medication-compatibility">
            {COMMON_MEDICATIONS.map((med, i) => (
              <div key={i} className="bg-white rounded-xl border border-border p-4">
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">{med.name}</h4>
                <p className="text-xs text-muted-foreground">{med.notes}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            Diabetes Lifestyle Strategies
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Evidence-based lifestyle modifications that complement your supplement protocol.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lifestyle-tips">
            {LIFESTYLE_TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm text-[#1A1A1A]">{tip.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.content}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-10 text-center" data-testid="symptom-tracker-cta">
          <h3 className="text-lg font-bold text-blue-900 mb-2">Track Your Blood Sugar Journey</h3>
          <p className="text-sm text-blue-700 mb-4 max-w-md mx-auto">
            Log fasting glucose, HbA1c, energy levels, and symptoms daily to track your progress and share with your endocrinologist.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-primary">
              <Link to="/symptoms">Start Tracking</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/journal">Open Journal</Link>
            </Button>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="grid grid-cols-2 gap-3 mb-10">
          <Link to="/quiz" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Pill className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Get Personalized Protocol</p>
            <p className="text-xs text-muted-foreground mt-1">Take the full assessment</p>
          </Link>
          <Link to="/ask" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Brain className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Ask AI About Diabetes</p>
            <p className="text-xs text-muted-foreground mt-1">Get supplement answers</p>
          </Link>
        </motion.div>
      </main>

      <GlobalFooter />
    </div>
  );
}
